import React, { useContext, useEffect, useMemo, useState } from 'react';
import AgentContext from '../../../context/admin/Agent.tsx';
import { useFeltLoading } from '../../../helpers/useFeltLoading.ts';
import { useUser } from '../../../context/UserContext.tsx';
import { Users } from '../../../models/User.tsx';
import { batchEditUsers, editUser, showUser } from '../../../services/admin/UserService.tsx';
import { logUserActivity } from '../../../services/player/LoginService.tsx';
import { logAgentActivity } from '../../../services/admin/AgentActivity.ts';
import {
    getWagerLimitsPreset,
    saveWagerLimitsPreset,
} from '../../../services/player/SportService.tsx';
import {
    ALL_PLAYER_LIMIT_FIELDS,
    LimitField,
    PLAYER_PAYOUT_LIMIT_FIELDS,
    PlayerLimitKey,
    WAGER_LIMIT_FIELDS,
    WagerLimitsForm,
    WagerLimitsValues,
    buildLimitsForm,
    buildLimitsFormFromValues,
    emptyLimitsForm,
    PLAYER_LIMIT_NOT_SET,
    formatLimitChange,
    formatLimitDisplay,
    formatOddsInputLive,
    formatWagerInputLive,
    getStatusClass,
    parseLimitsForm,
    parseOddsInput,
    parseWagerInput,
} from './bettingSetupUtils.ts';
import UnsavedChangesModal from './UnsavedChangesModal.tsx';

const PRESET_SELECTION_ID = '__all_players_preset__';

type PlayerLimitsSetupProps = {
    onDirtyChange: (dirty: boolean) => void;
    onToast: (type: 'success' | 'error', message: string) => void;
};

type PendingSelection = Users | 'preset' | null;

function PlayerLimitsSetup({ onDirtyChange, onToast }: PlayerLimitsSetupProps) {
    const { user: currentUser, userIpData: userIpDataAdmin } = useUser();
    const { allPlayers, allAgents, loading: rosterLoading, updatePlayerInContext } = useContext(AgentContext);

    const [searchQuery, setSearchQuery] = useState('');
    const [agentFilter, setAgentFilter] = useState('all');
    const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
    const [limitsForm, setLimitsForm] = useState<WagerLimitsForm>(emptyLimitsForm());
    const [originalLimits, setOriginalLimits] = useState<WagerLimitsForm>(emptyLimitsForm());
    const [presetLimits, setPresetLimits] = useState<WagerLimitsValues | null>(null);
    const [isLoadingPreset, setIsLoadingPreset] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingSelection, setPendingSelection] = useState<PendingSelection>(null);
    const showRosterLoading = useFeltLoading(rosterLoading);

    if (!currentUser) {
        throw new Error('User not authenticated');
    }

    const isMasterAgent = currentUser.userType === 'masterAgent';
    const isPresetSelected = selectedPlayerId === PRESET_SELECTION_ID;
    const presetScopeAgent = agentFilter === 'all' ? undefined : agentFilter;

    const playersInScope = useMemo(
        () =>
            (presetScopeAgent
                ? allPlayers.filter((player: Users) => player.parent === presetScopeAgent)
                : allPlayers
            ).filter((player: Users) => player.id),
        [allPlayers, presetScopeAgent]
    );

    const presetScopeLabel = presetScopeAgent ? `${presetScopeAgent}'s Players` : 'All Players';

    const filteredPlayers = useMemo(() => {
        let players = [...allPlayers];

        if (agentFilter !== 'all') {
            players = players.filter(p => p.parent === agentFilter);
        }

        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            players = players.filter(
                p =>
                    p.userName.toLowerCase().includes(q) ||
                    (p.name?.toLowerCase().includes(q) ?? false) ||
                    (p.parent?.toLowerCase().includes(q) ?? false)
            );
        }

        return players.sort((a, b) => a.userName.localeCompare(b.userName));
    }, [allPlayers, agentFilter, searchQuery]);

    const selectedPlayer = useMemo(
        () => (isPresetSelected ? null : filteredPlayers.find(p => p.id === selectedPlayerId) ?? null),
        [filteredPlayers, selectedPlayerId, isPresetSelected]
    );

    const hasUnsavedChanges = useMemo(
        () => ALL_PLAYER_LIMIT_FIELDS.some(({ key }) => limitsForm[key] !== originalLimits[key]),
        [limitsForm, originalLimits]
    );

    useEffect(() => {
        onDirtyChange(hasUnsavedChanges);
    }, [hasUnsavedChanges, onDirtyChange]);

    useEffect(() => {
        let cancelled = false;
        const loadPreset = async () => {
            setIsLoadingPreset(true);
            try {
                const preset = await getWagerLimitsPreset(presetScopeAgent);
                if (cancelled) return;
                setPresetLimits(preset);
                if (selectedPlayerId === PRESET_SELECTION_ID && !hasUnsavedChanges) {
                    const form = buildLimitsFormFromValues(preset);
                    setLimitsForm(form);
                    setOriginalLimits(form);
                }
            } catch (error) {
                console.error(error);
                if (!cancelled) onToast('error', 'Error loading wager limits preset');
            } finally {
                if (!cancelled) setIsLoadingPreset(false);
            }
        };
        loadPreset();
        return () => {
            cancelled = true;
        };
        // Reload the scoped preset whenever the agent filter changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetScopeAgent]);

    const applySelectPreset = () => {
        setSelectedPlayerId(PRESET_SELECTION_ID);
        const form = buildLimitsFormFromValues(presetLimits ?? {});
        setLimitsForm(form);
        setOriginalLimits(form);
        setPendingSelection(null);
    };

    const applySelectPlayer = (player: Users) => {
        if (!player.id) return;
        setSelectedPlayerId(player.id);
        const form = buildLimitsForm(player);
        setLimitsForm(form);
        setOriginalLimits(form);
        setPendingSelection(null);
    };

    const selectPreset = () => {
        if (!presetScopeAgent || isPresetSelected) return;
        if (hasUnsavedChanges) {
            setPendingSelection('preset');
            return;
        }
        applySelectPreset();
    };

    const selectPlayer = (player: Users) => {
        if (!player.id) return;
        if (hasUnsavedChanges) {
            setPendingSelection(player);
            return;
        }
        applySelectPlayer(player);
    };

    useEffect(() => {
        if (hasUnsavedChanges) return;
        if (!presetScopeAgent && isPresetSelected) {
            if (filteredPlayers[0]) applySelectPlayer(filteredPlayers[0]);
            else setSelectedPlayerId(null);
            return;
        }
        if (isPresetSelected) return;
        const stillVisible = filteredPlayers.some(player => player.id === selectedPlayerId);
        if (!stillVisible && filteredPlayers[0]) {
            applySelectPlayer(filteredPlayers[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredPlayers, selectedPlayerId, hasUnsavedChanges, isPresetSelected, presetScopeAgent]);

    const fieldByKey = (key: PlayerLimitKey) =>
        ALL_PLAYER_LIMIT_FIELDS.find(field => field.key === key)!;

    const handleLimitChange = (key: PlayerLimitKey, value: string) => {
        const kind = fieldByKey(key).kind;
        setLimitsForm(prev => ({
            ...prev,
            [key]: kind === 'odds' ? formatOddsInputLive(value) : formatWagerInputLive(value),
        }));
    };

    const handleLimitBlur = (key: PlayerLimitKey) => {
        const kind = fieldByKey(key).kind;
        const raw = limitsForm[key].trim().toLowerCase();
        if (raw === '' || raw === 'unlimited' || raw === 'not set' || raw === '0' || raw === '+0') {
            setLimitsForm(prev => ({ ...prev, [key]: PLAYER_LIMIT_NOT_SET }));
            return;
        }
        const parsed = kind === 'odds' ? parseOddsInput(limitsForm[key]) : parseWagerInput(limitsForm[key]);
        if (parsed > 0) {
            setLimitsForm(prev => ({ ...prev, [key]: formatLimitDisplay(key, kind, parsed) }));
        }
    };

    const handleReset = () => {
        setLimitsForm({ ...originalLimits });
    };

    const handleSavePreset = async () => {
        if (!presetLimits) return;
        setIsSaving(true);
        try {
            const newData = parseLimitsForm(limitsForm);
            const changedFields: Partial<WagerLimitsValues> = {};
            const changes = ALL_PLAYER_LIMIT_FIELDS
                .filter(({ key }) => newData[key] !== presetLimits[key])
                .map(({ key, kind }) => {
                    const oldValue = presetLimits[key] ?? 0;
                    const newValue = newData[key];
                    changedFields[key] = newValue;
                    return `${key}: ${formatLimitChange(key, kind, oldValue)} → ${formatLimitChange(key, kind, newValue)}`;
                });

            if (changes.length === 0) {
                onToast('success', 'No changes to save');
                return;
            }

            const nextPreset = { ...presetLimits, ...changedFields };
            await saveWagerLimitsPreset(nextPreset, presetScopeAgent);
            setPresetLimits(nextPreset);

            const playerIds = playersInScope.map(player => player.id!);
            await batchEditUsers(playerIds, changedFields);
            playerIds.forEach(id => updatePlayerInContext(id, changedFields));

            if (currentUser?.userName) {
                await logUserActivity(
                    currentUser.userName,
                    `Betting Setup Preset (${presetScopeLabel}): ${changes.join(', ')} | applied to ${playersInScope.length} players`,
                    userIpDataAdmin
                );
                void logAgentActivity({
                    userName: currentUser.userName,
                    parent: currentUser.parent,
                    activity: 'Change limits',
                    customer: presetScopeLabel,
                    detail: `Applied to ${playersInScope.length} players | ${changes.join(', ')}`,
                });
            }

            const updatedForm = buildLimitsFormFromValues(nextPreset);
            setLimitsForm(updatedForm);
            setOriginalLimits(updatedForm);
            onToast(
                'success',
                `Limits applied to ${playersInScope.length} player${playersInScope.length === 1 ? '' : 's'} (${presetScopeLabel})`
            );
        } catch (error) {
            console.error(error);
            onToast('error', 'Error saving wager limits for these players');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSavePlayer = async () => {
        if (!selectedPlayer?.id) return;

        setIsSaving(true);
        try {
            const freshUser = await showUser(selectedPlayer.userName);
            const newData = parseLimitsForm(limitsForm);

            const changes: string[] = [];
            ALL_PLAYER_LIMIT_FIELDS.forEach(({ key, kind }) => {
                const oldValue = freshUser[key] ?? 0;
                const newValue = newData[key];
                if (oldValue !== newValue) {
                    changes.push(
                        `${key}: ${formatLimitChange(key, kind, oldValue)} → ${formatLimitChange(key, kind, newValue)}`
                    );
                }
            });

            if (changes.length === 0) {
                onToast('success', 'No changes to save');
                return;
            }

            await editUser(selectedPlayer.id, newData);

            if (currentUser?.userName) {
                await logUserActivity(
                    currentUser.userName,
                    `Betting Setup: ${selectedPlayer.userName} | ${changes.join(', ')}`,
                    userIpDataAdmin
                );
                void logAgentActivity({
                    userName: currentUser.userName,
                    parent: currentUser.parent,
                    activity: 'Change limits',
                    customer: selectedPlayer.userName,
                    detail: changes.join(', '),
                });
            }

            updatePlayerInContext(selectedPlayer.id, newData);

            const updatedForm = buildLimitsForm({ ...selectedPlayer, ...newData });
            setLimitsForm(updatedForm);
            setOriginalLimits(updatedForm);
            onToast('success', `Limits updated for ${selectedPlayer.userName}`);
        } catch (error) {
            console.error(error);
            onToast('error', 'Error saving wager limits');
        } finally {
            setIsSaving(false);
        }
    };

    const renderLimitCards = (fields: LimitField[]) => (
        <div className="betting-setup-limits-grid">
            {fields.map(({ key, label, icon, description, kind }) => (
                <div key={key} className="betting-setup-limit-card">
                    <div className="betting-setup-limit-label">
                        <span className="betting-setup-limit-icon">{icon}</span>
                        {label}
                    </div>
                    <div className="betting-setup-limit-desc">{description}</div>
                    <div className="betting-setup-limit-input-wrap">
                        {kind === 'money' && <span className="betting-setup-limit-prefix">$</span>}
                        <input
                            type="text"
                            className="betting-setup-limit-input"
                            value={limitsForm[key]}
                            onChange={e => handleLimitChange(key, e.target.value)}
                            onBlur={() => handleLimitBlur(key)}
                            autoComplete="off"
                            disabled={isSaving}
                        />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <>
            <div className="betting-setup-toolbar betting-setup-toolbar-inline">
                {isMasterAgent && allAgents.length > 0 && (
                    <select
                        className="betting-setup-agent-select"
                        value={agentFilter}
                        onChange={e => setAgentFilter(e.target.value)}
                    >
                        <option value="all">All Agents</option>
                        {allAgents.map((agent: Users) => {
                            const nested = agent.parent && agent.parent !== currentUser.userName;
                            const label = nested ? `${agent.userName} (under ${agent.parent})` : agent.userName;
                            return (
                                <option key={agent.id ?? agent.userName} value={agent.userName}>
                                    {label}
                                </option>
                            );
                        })}
                    </select>
                )}
                <input
                    type="text"
                    className="betting-setup-search"
                    placeholder="Search players..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    autoComplete="off"
                />
            </div>

            <div className="betting-setup-layout">
                <div className="betting-setup-players-panel">
                    <div className="betting-setup-panel-header">
                        <span>Players</span>
                        <span className="betting-setup-player-count">{filteredPlayers.length}</span>
                    </div>
                    <div className="betting-setup-players-list">
                        {presetScopeAgent && (
                        <div
                            className={`betting-setup-player-item betting-setup-preset-item ${isPresetSelected ? 'active' : ''}`}
                            onClick={selectPreset}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => e.key === 'Enter' && selectPreset()}
                        >
                            <div className="betting-setup-player-info">
                                <span className="betting-setup-player-name">{presetScopeLabel}</span>
                                <span className="betting-setup-player-agent">
                                    Update every player of {presetScopeAgent}
                                </span>
                            </div>
                            <span className="betting-setup-player-status preset">All</span>
                        </div>
                        )}
                        {showRosterLoading ? (
                            Array.from({ length: 8 }, (_, index) => (
                                <div key={index} className="betting-setup-player-item" aria-hidden="true">
                                    <span className="rect-placeholder admin-sk" style={{ width: 120, height: 14 }} />
                                </div>
                            ))
                        ) : filteredPlayers.length === 0 ? (
                            <div className="betting-setup-empty">No players found</div>
                        ) : (
                            filteredPlayers.map(player => (
                                <div
                                    key={player.id}
                                    className={`betting-setup-player-item ${selectedPlayerId === player.id ? 'active' : ''}`}
                                    onClick={() => selectPlayer(player)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => e.key === 'Enter' && selectPlayer(player)}
                                >
                                    <div className="betting-setup-player-info">
                                        <span className="betting-setup-player-name">{player.userName}</span>
                                        {player.name && (
                                            <span className="betting-setup-player-agent">{player.name}</span>
                                        )}
                                        {isMasterAgent && player.parent && (
                                            <span className="betting-setup-player-agent">Agent: {player.parent}</span>
                                        )}
                                    </div>
                                    <span className={`betting-setup-player-status ${getStatusClass(player.status)}`}>
                                        {player.status}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="betting-setup-editor-panel">
                    <div className="betting-setup-panel-header">
                        <span>Wager Limits</span>
                    </div>

                    {isLoadingPreset && isPresetSelected ? (
                        <div className="betting-setup-empty">Loading limits...</div>
                    ) : isPresetSelected ? (
                        <div className="betting-setup-editor-content">
                            <div className="betting-setup-selected-user">
                                <div>
                                    <div className="betting-setup-selected-name">{presetScopeLabel}</div>
                                    <div className="betting-setup-selected-meta">
                                        Applies to {playersInScope.length} player{playersInScope.length === 1 ? '' : 's'}
                                        {presetScopeAgent ? ` of ${presetScopeAgent}` : ''}
                                    </div>
                                </div>
                                <span className="betting-setup-player-status preset">All</span>
                            </div>
                            <p className="betting-setup-hint">
                                Only fields you change are written to every player
                                {presetScopeAgent ? ` of ${presetScopeAgent}` : ''}. The other limits stay as each player has them now.
                            </p>
                            {renderLimitCards(WAGER_LIMIT_FIELDS)}
                            <div className="betting-setup-section-label">Payout &amp; odds</div>
                            {renderLimitCards(PLAYER_PAYOUT_LIMIT_FIELDS)}
                            <div className="betting-setup-actions">
                                {hasUnsavedChanges && (
                                    <span className="betting-setup-unsaved">Unsaved changes</span>
                                )}
                                <button
                                    type="button"
                                    className="betting-setup-btn betting-setup-btn-reset"
                                    onClick={handleReset}
                                    disabled={!hasUnsavedChanges || isSaving}
                                >
                                    Reset
                                </button>
                                <button
                                    type="button"
                                    className="betting-setup-btn betting-setup-btn-save"
                                    onClick={handleSavePreset}
                                    disabled={!hasUnsavedChanges || isSaving || isLoadingPreset}
                                >
                                    {isSaving ? 'Saving...' : 'Save for all'}
                                </button>
                            </div>
                        </div>
                    ) : !selectedPlayer ? (
                        <div className="betting-setup-empty">Select a player to edit wager limits</div>
                    ) : (
                        <div className="betting-setup-editor-content">
                            <div className="betting-setup-selected-user">
                                <div>
                                    <div className="betting-setup-selected-name">{selectedPlayer.userName}</div>
                                    <div className="betting-setup-selected-meta">
                                        {selectedPlayer.name && `${selectedPlayer.name} · `}
                                        Agent: {selectedPlayer.parent ?? 'N/A'}
                                    </div>
                                </div>
                                <span className={`betting-setup-player-status ${getStatusClass(selectedPlayer.status)}`}>
                                    {selectedPlayer.status}
                                </span>
                            </div>

                            <p className="betting-setup-hint">
                                Enter 0 or &quot;Not set&quot; when this player has no own limit. A number overrides the agent.
                            </p>

                            {renderLimitCards(WAGER_LIMIT_FIELDS)}
                            <div className="betting-setup-section-label">Payout &amp; odds</div>
                            {renderLimitCards(PLAYER_PAYOUT_LIMIT_FIELDS)}

                            <div className="betting-setup-actions">
                                {hasUnsavedChanges && (
                                    <span className="betting-setup-unsaved">Unsaved changes</span>
                                )}
                                <button
                                    type="button"
                                    className="betting-setup-btn betting-setup-btn-reset"
                                    onClick={handleReset}
                                    disabled={!hasUnsavedChanges || isSaving}
                                >
                                    Reset
                                </button>
                                <button
                                    type="button"
                                    className="betting-setup-btn betting-setup-btn-save"
                                    onClick={handleSavePlayer}
                                    disabled={!hasUnsavedChanges || isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <UnsavedChangesModal
                show={pendingSelection !== null}
                onKeepEditing={() => setPendingSelection(null)}
                onDiscard={() => {
                    if (pendingSelection === 'preset') {
                        applySelectPreset();
                        return;
                    }
                    if (pendingSelection) applySelectPlayer(pendingSelection);
                }}
            />
        </>
    );
}

export default PlayerLimitsSetup;
